import type {AllEventsMap, CreateEventButtonCommonProps, EventCalendarMetaData, EventHashedID, EventsFilters, EventsListLazyData, EventsViewType, EventVO} from '@Components/events/events.types';
import {UserEventCreationPermissionState} from '@Components/events/events.types';
import type {LoadingStates} from '@Utils/loading.util';
import {isLoaded, isLoadingOrLoaded, isLoadingOrNeedsLoading} from '@Utils/loading.util';
import {getEventsLazyDataBasedOnAppliedFilters, openUpsellDialogForEventsLimitReached} from '@Libraries/events.library';
import {isPaidTicketingFeatureAvailable} from '@Libraries/event-ticketing.library';
import type {MouseEvent, RefObject} from 'react';
import {useEffect, useState} from 'react';
import {getEventsList} from '@Components/events/events-thunk';
import {triggerEventsLazyLoadOnScroll} from '@Components/events/events-slice';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {Type} from '@Components/button';
import {EventsCreateGA4Source, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';

export const useEventsCurrentViewType = (): EventsViewType => {
  return useAppSelector(({events}) => {
    return events.eventsViewType;
  });
};

export const useIsEventsEmptyState = (): boolean => {
  return useAppSelector(({events}) => {
    return isLoaded(events.eventsListDefaultLazyData.loadingState) && events.eventsListDefaultLazyData.ids.length === 0;
  });
};
export const useIsEventsListViewSelected = (): boolean => {
  return useAppSelector(({events}) => {
    return events.isListViewSelected;
  });
};

export const useEventsFilters = (): EventsFilters => {
  return useAppSelector(({events}) => {
    return events.filters;
  });
};

export const useFilteredEventsForListView = (): EventVO[] => {
  const allEvents = useAllEvents();
  const lazyData = useEventsLazyDataBasedOnFiltersApplied();

  const events: EventVO[] = [];
  lazyData.ids.forEach((id) => {
    if (allEvents[id]) {
      events.push(allEvents[id]);
    }
  });

  return events;
};

export const useAllEvents = (): AllEventsMap => {
  return useAppSelector(({events}) => {
    return events.allEvents;
  });
};

export const useEventsLazyDataBasedOnFiltersApplied = (): EventsListLazyData => {
  return useAppSelector(({events}) => {
    return getEventsLazyDataBasedOnAppliedFilters(events, events.eventsViewType, events.filters);
  });
};

export const useEventsLoadingState = (): LoadingStates => {
  return useAppSelector(({events}) => {
    return getEventsLazyDataBasedOnAppliedFilters(events, events.eventsViewType, events.filters).loadingState;
  });
};

export const useEventsMetaDataLoadingState = (): LoadingStates => {
  return useAppSelector(({events}) => {
    return events.pageMetaDataLoadingState;
  });
};

export const useIsEventsMetadataLoading = (): boolean => {
  const loadingState = useEventsMetaDataLoadingState();
  return isLoadingOrNeedsLoading(loadingState);
};

export const useIsEventsCalendarSectionLoading = (): boolean => {
  return useAppSelector(({events}) => {
    return isLoadingOrNeedsLoading(events.calendarSectionLoadingState);
  });
};

export const useShouldShowEventsScheduleEmbedBanner = (): boolean => {
  return useAppSelector(({events}) => {
    return events.doShowEmbedBanner;
  });
};

/**
 * The Stripe (payments) tab needs BOTH gates: paid ticketing has to be available to this viewer at all, and the
 * organiser has to have set ticketing up before. The feature gate comes first so the tab stays hidden everywhere
 * paid ticketing isn't live, even for an organiser who already has a Hi.Events account.
 */
export const useShouldShowEventsStripeTab = (): boolean => {
  const hasEverSetUpTicketing = useAppSelector(({events}) => {
    return events.doShowStripeTab;
  });
  return isPaidTicketingFeatureAvailable() && hasEverSetUpTicketing;
};

export const useAreEventsLoading = (): boolean => {
  const loadingState = useEventsLoadingState();
  return isLoadingOrNeedsLoading(loadingState);
};

export const useEventFromId = (eventId: EventHashedID): EventVO|undefined => {
  return useAppSelector(({events}) => {
    return events.allEvents[eventId];
  });
};

export const useUserEventCreationPermissionState = (): UserEventCreationPermissionState => {
  return useAppSelector(({events}) => {
    return events.userEventCreationPermissionState;
  });
};

export const useDoesUserNeedUpsellForEvents = (): boolean => {
  return useAppSelector(({events}) => {
    return events.userEventCreationPermissionState === UserEventCreationPermissionState.NEEDS_UPSELL;
  });
};

export const useInitEventsListLazyLoading = (gridContainerRef: RefObject<HTMLDivElement | null>): void => {
  const dispatch = useAppDispatch();
  const eventsViewType = useEventsCurrentViewType();
  const eventFilters = useEventsFilters();
  const lazyDataBasedOnFilters = useEventsLazyDataBasedOnFiltersApplied();

  useEffect(() => {
    if (isLoadingOrLoaded(lazyDataBasedOnFilters.loadingState) || !lazyDataBasedOnFilters.loadMore) {
      return;
    }

    void dispatch(
      getEventsList({
        eventsViewType,
        timeFilters: eventFilters.timeFilter,
        sort: eventFilters.sortType,
        searchTerm: eventFilters.searchTerm,
        offset: lazyDataBasedOnFilters.ids.length,
        sharedEventFilter: eventFilters.sharedEventsActiveFilter
      })
    );
  }, [eventFilters, eventsViewType, lazyDataBasedOnFilters]);

  useEffect(() => {
    const onScroll = (): void => {
      if (!gridContainerRef.current) {
        return;
      }

      if (gridContainerRef.current.getBoundingClientRect().bottom - 100 <= window.innerHeight) {
        dispatch(triggerEventsLazyLoadOnScroll());
      }
    };
    const debouncedHandler = window.PMW.debounce(onScroll, 100) as VoidFunction;

    window.addEventListener('scroll', debouncedHandler, {passive: true});

    return () => {
      window.removeEventListener('scroll', debouncedHandler);
    };
  }, [gridContainerRef.current]);
};

export const useCreateEventButtonCommonProps = (): CreateEventButtonCommonProps => {
  const userPermissionsState = useUserEventCreationPermissionState();
  const [isLoading, setIsLoading] = useState(false);
  const onClick = (e: MouseEvent): void => {
    if (userPermissionsState === UserEventCreationPermissionState.VALIDATING_PERMISSIONS) {
      return;
    }

    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_CLICK, {[GA4EventParamName.SOURCE]: EventsCreateGA4Source.MY_STUFF});
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_MYSTUFF_CREATE);
    setIsLoading(true);
    if (userPermissionsState === UserEventCreationPermissionState.NEEDS_UPSELL) {
      e.preventDefault();
      openUpsellDialogForEventsLimitReached(() => {
        setIsLoading(false);
      });
      return;
    }

    window.location.href = window.PMW.util.site_url('event/wizard');
  };

  return {
    onClick,
    isLoading,
    buttonType: Type.PRIMARY,
    showPremiumUpsellingIcon: userPermissionsState === UserEventCreationPermissionState.NEEDS_UPSELL,
  };
};

export const useEventCalendarMetaData = (): EventCalendarMetaData|undefined => {
  return useAppSelector(({events}) => {
    return events.eventCalendarMetaData;
  });
}